import { View } from "@/components/ui/view";
import { HStack } from "@/components/ui/hstack";
import { VStack } from "@/components/ui/vstack";
import { Text } from "@/components/ui/text";
import { Pressable } from "@/components/ui/pressable";
import { Popover, PopoverBackdrop, PopoverContent, PopoverHeader, PopoverBody } from "@/components/ui/popover";
import { Divider } from "@/components/ui/divider";
import React, { useContext, useState } from 'react';
import i18n from "i18n-js";
import _ from 'lodash';
import { MediaContext } from "../media/MediaContext";
import InputHandle from "../basic/InputHandle";
import OutputHandle from "../basic/OutputHandle";
import NodeView from "./NodeView";
import ChangeableText from "../basic/ChangeableText";
import MediaUpload from "../media/MediaUpload";
import { guid7 } from "@/containers/GlobalState";
import DraggableFlatList, { ScaleDecorator } from 'react-native-draggable-flatlist';
import { IconM } from '@/components/media/IconsV2';

export default function ChecklistNode({ id, data, selected }) {
    const { media, setMedia, flowId, nodeHighlighting } = useContext(MediaContext)
    const elements = data.elements || []
    const listElementMedia = data.listElementMedia || {}

    const [elementPopover, setElementPopover] = useState(false)

    const renderItem = ({ item, getIndex, drag, isActive }) => {
        const index = getIndex()
        const [title, setTitle] = useState(item.title)
        const [description, setDescription] = useState(item.description)

        const onChangeElementTitle = (text, deleted) => {
            setTitle(text)
        }

        const onChangeElementDescription = (text, deleted) => {
            setDescription(text)
        }

        const onDeleteElement = () => {
            let newElements = _.cloneDeep(elements)
            newElements.splice(index, 1)
            let tempListElementMedia = _.cloneDeep(listElementMedia)
            delete tempListElementMedia[item.id ? item.id : id + 'element' + index]
            const newNodeData = { ...data, elements: newElements, listElementMedia: tempListElementMedia }
            data.updateNode(id, newNodeData, true)
        }

        const onClose = () => {
            setElementPopover(false)
            let newElements = _.cloneDeep(elements)
            newElements[index] = { ...newElements[index], title: title, description: description }
            data.updateNode(id, { ...data, elements: newElements }, true)
        }

        return (
            <ScaleDecorator>
                <Popover trigger={(triggerProps) => {
                    return (
                        <VStack className={` ${data.disabled ? 'rounded-md' : ''} ${elementPopover === index ? "bg-primary-400" : "bg-primary-50"} `}>
                            <Divider />
                            <Pressable {...triggerProps} onPress={() => { setElementPopover(index) }} onLongPress={drag} dataSet={{ nodrag: 1 }} className="py-3 px-4">
                                <Text className="text-md text-secondary-900">{i18n.t('showItemScreen.title')} {index + 1}: {title}</Text>
                            </Pressable>
                        </VStack>
                    );
                }}
                    isOpen={elementPopover === index}
                    onOpen={() => console.log('OPEN', x)}
                    onClose={() => onClose()}
                    placement='right'
                    offset={8}
                >
                    <MediaContext.Provider value={{ media, setMedia }}>
                        <PopoverBackdrop />
                        <PopoverContent className='min-w-[260px] border border-primary-900 rounded-[4px] p-0'>
                            <PopoverHeader className="bg-primary-100">
                                <Text className="text-md text-secondary-900 max-w-[200px] p-2">{i18n.t('showItemScreen.title')} {index + 1}</Text>
                                <HStack className="absolute top-1 right-1" >
                                {flowId != 'showFlow' && <View className="bg-primary-50 border border-primary-900 rounded-[4px] p-1 mr-[2px]">
                                    <IconM className='text-primary-900' size={22} name='delete' />
                                    <Pressable
                                        onPress={() => {
                                            setElementPopover(false)
                                            onDeleteElement()
                                        }}
                                        className="absolute w-8 h-8" />
                                </View>}
                                <View className="bg-primary-50 border border-primary-900 rounded-[4px] p-1 ml-[2px]">
                                    <IconM className='text-primary-900' size={22} name='close' />
                                    <Pressable onPress={onClose} className="absolute w-8 h-8" />
                                </View>
                            </HStack>
                            </PopoverHeader>
                            <Divider />
                            <PopoverBody className='p-0'>
                                <VStack>
                                    {flowId == 'showFlow' ?
                                        <Text className="px-3 text-md text-secondary-900">{title}</Text> :
                                        <ChangeableText
                                            text={title}
                                            placeholder={i18n.t('itemInput.title')}
                                            updateText={onChangeElementTitle}
                                            fontSize='text-sm'
                                            fontWeight='font-bold'
                                        />}
                                    <Divider />
                                    {flowId == 'showFlow' ?
                                        <Text className="px-3 text-md text-secondary-900">{description}</Text> :
                                        <ChangeableText
                                            text={description}
                                            fontSize='text-xs'
                                            placeholder={i18n.t('itemInput.text')}
                                            updateText={onChangeElementDescription}
                                        />}
                                    <Divider />
                                    <MediaUpload fontSize='text-sm' nodeId={item.id ? item.id : id + 'element' + index} data={data} disabled={flowId == 'showFlow'} flowIdForElement={flowId} />
                                </VStack>
                            </PopoverBody>
                        </PopoverContent>
                    </MediaContext.Provider>
                </Popover>
            </ScaleDecorator >
        );
    }

    const addElement = () => {
        let newElements = _.cloneDeep(elements)
        newElements.push({ id: id + 'element' + guid7().substring(0, 7), title: "", description: "" })
        const newNodeData = { ...data, elements: newElements }
        data.updateNode(id, newNodeData, true)
        setElementPopover(newElements.length - 1)
    }

    const sortElements = (newElements, from, to) => {
        if (from != to) {
            data.updateNode(id, { ...data, elements: newElements }, true)
        }
    }

    return (
        <NodeView id={id} data={data} selected={selected} disabled={flowId == 'showFlow'} nodeHighlighting={nodeHighlighting[id]}>
            <DraggableFlatList
                data={elements}
                renderItem={renderItem}
                onDragEnd={({ data, from, to }) => sortElements(data, from, to)}
                keyExtractor={(item, index) => item.title + index}
            />
            {!data.disabled && <Divider />}
            {!data.disabled && <Pressable onPress={() => addElement()} className="p-4 self-center">
                <Text className="font-bold text-xl text-primary-900">+ {i18n.t('stepEdit.addItem')}</Text>
            </Pressable>}
            <InputHandle nodeHighlighting={nodeHighlighting[id]} />
            <OutputHandle id='output' position={28} nodeHighlighting={nodeHighlighting[id]} />
        </NodeView>
    );
}